/**
 * RouteGuard - Centralized permission middleware for authenticated routes
 *
 * Wraps all authenticated routes and checks permissions based on the current pathname.
 * Uses the same two-level check as the sidebar:
 *   1. Module permission (e.g. 'dashboard.view')
 *   2. Submodule permission (e.g. dashboard.agent)
 *
 * Behavior:
 * - Public/unguarded route (null) -> allows access
 * - DENY_ACCESS sentinel -> shows AccessDeniedPage (route without configured permissions)
 * - Loading permissions -> shows spinner
 * - Has both module + submodule permission -> renders children
 * - Missing either permission -> shows AccessDeniedPage
 */

import { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { findPermissionForPath, DENY_ACCESS } from './route-permissions'
import { AccessDeniedPage } from '@/shared/components/AccessDeniedPage'

interface RouteGuardProps {
  children: ReactNode
}

export function RouteGuard({ children }: RouteGuardProps) {
  const location = useLocation()
  const { hasPermission, hasSubmodulePermission, isLoading } = usePermissions()

  const routePermission = findPermissionForPath(location.pathname)

  // No permission mapping for this path (public/unguarded) -> allow access
  if (!routePermission) {
    return <>{children}</>
  }

  // Route has no configured permissions -> deny by default
  if (routePermission === DENY_ACCESS) {
    return (
      <AccessDeniedPage
        message="Esta ruta no tiene permisos configurados. Contacta al equipo de desarrollo."
      />
    )
  }

  // Permissions still loading -> show spinner
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Verificando permisos...
          </p>
        </div>
      </div>
    )
  }

  // 1. Check module-level permission (e.g. 'dashboard.view')
  if (!hasPermission(routePermission.permission)) {
    const [module, action] = routePermission.permission.split('.')
    return <AccessDeniedPage module={module} action={action} />
  }

  // 2. Check submodule permission (e.g. dashboard.agent) - same as sidebar
  if (routePermission.submodule) {
    const { module, submodule } = routePermission.submodule
    if (!hasSubmodulePermission(module, submodule)) {
      return <AccessDeniedPage module={module} />
    }
  }

  return <>{children}</>
}
